import { EventEmitter, inject, Injectable, output } from '@angular/core';
import { DCLCardOptions } from '@portal/clients/dcl-cards/entities/dcl-card.options';
import {
    DigitalClientCardInputType,
    DigitalClientCardGraphQLSchemaActions,
    DigitalClientCardGraphQLSchemaSelectors,
    DigitalClientCardTypeQuery,
    DCLCorrelationTypeQuery,
    DCLGarageUseTypeQuery,
    DCLParkingCarWashUseTypeQuery,
    DCLRentalUseTypeQuery,
    DCLResponseTypeQuery,
    InvoiceTypeQuery,
    ProviderResponseErrorTypeQuery,
    ProviderResponseTypeQuery,
    ResponseTypeErrorTypeQuery,
    UsersActionsTypeQuery,
    DigitalClientCardType,
    InvoiceType,
    POSTerminalResponseType,
    POSTerminalTransactionType,
    DigitalClientCardTypeFormGroup,
    CustomerTypeQuery,
    AddressTypeQuery,
    CityTypeQuery,
    ContactDetailTypeQuery,
    CustomersToEntitiesSubjectTypeQuery,
    EntitySubjectInfoTypeQuery,
    FinancialDetailTypeQuery,
    FirstNameTypeQuery,
    InfoNoteTypeQuery,
    OccupationActivityTypeQuery,
    TAXOfficeTypeQuery,
    TraderProfitMarginTypeQuery
} from '@shared/zigra-schemas/zigra-schema.autogenerated.models';
import { concatMap, of, finalize, timer, Observable, takeUntil } from 'rxjs';
import { LoaderService } from '../loader.service';
import { Store } from '@ngxs/store';
import { InvoiceCardFooterComponent } from '@portal/invoicing/invoices-cards/components/footer/invoice-card-footer/invoice-card-footer.component';
import { InvoiceCardComponent } from '@portal/invoicing/invoices-cards/components/invoice-card/invoice-card.component';
import { InvoiceOptions } from '@portal/invoicing/invoices-cards/entities/invoice-card.options';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { PostDCLsToAADESchema } from '@shared/zigra-schemas/custom/PostDCLsToAADE.schema.service';
import { ToastMessageOptions } from 'primeng/api';
import { ZionMessagesService } from '../ax-messages.service';
import { digitalClientCardsQuery } from '@portal/clients/dcl-cards/dcl-cards.component';
import { QueryFilter } from 'zigra-ng-framework';

@Injectable({
    providedIn: 'root'
})
export class DclCardService {
    loaderService = inject(LoaderService);
    store = inject(Store);
    dialogService = inject(DialogService);
    messageService = inject(ZionMessagesService);
    postDCLsToAADESchema = inject(PostDCLsToAADESchema);

    refreshData: EventEmitter<boolean> = new EventEmitter<boolean>();

    constructor() {}

    /**
     * Ενημέρωσε το Ψηφιακό Πελατολόγιο με τον τύπο του παραστατικού που θα εκδόσεις και εμφάνισε το Modal των παραστατικών.
     * Κάνει έξοδο το όχημα αν υπάρχει ήδη έστω και ένα παραστατικό που έχει παρει ΜΑΡΚ ή μετά την έκδοση του 1ου Παραστατικού
     * @param invoiceType
     * @param dclFormGroup
     * @param source
     */
    updateInvoiceKindOrCompleteDCL(
        invoiceType: 'invoice' | 'receipt',
        dclCardsDialogConfigDataOptions: DCLCardOptions,
        dclFormGroup?: DigitalClientCardTypeFormGroup,
        source?: 'dcl-modal'
    ) {
        debugger;

        const digitalClientCardUnsetted = dclFormGroup?.getRawValue() as
            | DigitalClientCardInputType
            | undefined;

        // Αν υπάρχει έστω και ένα Παραστατικό που έχει πάρει Μ.ΑΡ.Κ τότε δεν αλλάζουμε InvoiceKind
        // και απλά κάνουμε completeDCL().
        const issuedInvoicesCount =
            digitalClientCardUnsetted?.Invoices?.filter(
                (invoice) => (invoice.Response?.InvoiceMark ?? 0) > 0
            ).length ?? 0;

        // Απλά κάνε έξοδο το οχημα.
        // Τo παραστατικό θα το δέσουμε από μέσα από το παραστατικό ή από τον πίνακα των Ψηφιακών Εγγραφών Πελατολογίου
        // γιατι έτσι ξέρουμε τι τύπο παραστατικού θέλουμε.
        // Είναι η περίπτωση που ο πελάτης πει θέλω ΤΙμολόγιο αλλά μετά πει όχι θέλω απόδειξη. Οπότε πριν ολοκλήρωθεί η πρώτη έκδοση δεν ολοκληρώνεται η έξοδος του οχήαμτος.
        if (issuedInvoicesCount > 0) {
            return this.completeDCL(dclCardsDialogConfigDataOptions).pipe(
                concatMap((updatedDigitalClientCard) => {
                    const invoices = updatedDigitalClientCard?.Invoices ?? [];

                    if (updatedDigitalClientCard != undefined) {
                        if (invoices.length == 0)
                            // ΠΡΟΣΟΧΗ: Όταν ανοίξει η φόρμα του νεόυ παραστατικού και ο χρήστης πατήσει έκδοση/διαβίβαση παραστατικού δίχως να έχει επιλέξει τρόπο πληρωμής
                            // το σύστημα πρέπει να του ζητήσει τρόπο πληρωμής και μετά να συνεχίσει στην έκδοση/διαβίβαση.
                            this.newInvoice(
                                dclCardsDialogConfigDataOptions,
                                updatedDigitalClientCard,
                                invoiceType == 'invoice' ? '2.1' : '11.2' // 11.2 ΑΠΥ
                            );
                        else {
                            this.editInvoice(
                                dclCardsDialogConfigDataOptions,
                                updatedDigitalClientCard
                            );
                        }
                    }
                    //  value?.data.UpdateDigitalClientCard
                    return of(updatedDigitalClientCard);
                })
            );
        }

        this.loaderService.showAwaitLoader(
            'Ενημέρωση Ψηφιακού Πελατολογίου. Παρακαλώ Περιμένετε...'
        );
        // Όρισε τον τύπο του Παραστατικού που θέλει ο πελάτης.
        // ΠΡΟΣΟΧΗ: Αυτό μπορεί να γίνει και από άλλο σημείο της εφαρμογής, όταν ο χρήστης δέσει εξωτερικά ένα παραστατικό με μια εγργαφή ψηφιακού πελατολογίου.
        dclFormGroup?.controls?.InvoiceKind?.setValue(
            invoiceType == 'invoice' ? 2 : 1,
            { emitEvent: false }
        );

        const digitalClientCard = dclFormGroup?.getRawValue() as
            | DigitalClientCardInputType
            | undefined;

        // Ενημέρωσε την εγγραφή στη βάση μας με τον τύπο του παραστατικου που θέλει να εκδόσει ο πελάτης
        return this.store
            .dispatch(
                new DigitalClientCardGraphQLSchemaActions.Upsert(
                    digitalClientCard,
                    undefined,
                    this.digitalClientCardGQLQueryForEdit
                )
            )
            .pipe(
                concatMap(() => {
                    // dclCardsDialogConfigData?.onDCLCardSave(
                    //     dclCardsDialogConfigData
                    // );
                    // Εδώ πρέπει να φορτώσουμε τα δυο παραστατικά
                    let updatedDigitalClientCard = this.store.selectSnapshot(
                        DigitalClientCardGraphQLSchemaSelectors.UpsertSelector(
                            '',
                            ''
                        )
                    )?.data.Upsert;

                    const invoices = updatedDigitalClientCard?.Invoices ?? [];
                    if (updatedDigitalClientCard != undefined) {
                        if (invoices.length == 0)
                            // ΠΡΟΣΟΧΗ: Όταν ανοίξει η φόρμα του νεόυ παραστατικού και ο χρήστης πατήσει έκδοση/διαβίβαση παραστατικού δίχως να έχει επιλέξει τρόπο πληρωμής
                            // το σύστημα πρέπει να του ζητήσει τρόπο πληρωμής και μετά να συνεχίσει στην έκδοση/διαβίβαση.
                            this.newInvoice(
                                dclCardsDialogConfigDataOptions,
                                updatedDigitalClientCard,
                                invoiceType == 'invoice' ? '2.1' : '11.2' // 11.2 ΑΠΥ
                            );
                        else {
                            this.editInvoice(
                                dclCardsDialogConfigDataOptions,
                                updatedDigitalClientCard
                            );
                        }
                    }

                    return of(updatedDigitalClientCard);
                }),
                finalize(() => this.loaderService.dismissAwaitLoader())
            );

        // this.dclCardService
        //     .completeDCL(
        //         invoiceType,
        //         this.dclFormGroup,
        //         this.dialogConfig as DCLCardOptions
        //     )
        //     ?.subscribe();
    }

    bindCDLWithInvoices(
        invoiveType: 'invoice' | 'receipt',
        dclOptions?: DCLCardOptions
    ) {
        const options = dclOptions as DCLCardOptions;
        const digitalClientCard =
            options.digitalClientCardTypeFormGroup?.getRawValue() as
                | DigitalClientCardInputType
                | undefined;

        if (digitalClientCard)
            this.newInvoice(
                options,
                digitalClientCard,
                invoiveType == 'invoice' ? '2.1' : '11.2'
            ); // 2.1: Τιμολόγιο Παροχής, 11.2: Α.Π.Υ
    }

    /**
     * Ολοκλήρωνει την εγγραφή του Ψηφιακού πελατολογίου με την έξοδο του Οχήματος (digitalClientCard.EntryCompletion = true).
     * Έχει έρθει από το dcl-card.service.ts
     * @param dclOptions
     * @param invoice
     * @returns
     */
    completeDCL(dclOptions: DCLCardOptions) {
        debugger;
        const options = dclOptions; // as DCLCardOptions;

        const digitalClientCard =
            options.digitalClientCardTypeFormGroup?.getRawValue() as
                | DigitalClientCardType
                | undefined;

        if (
            digitalClientCard == undefined ||
            digitalClientCard._id == undefined
        )
            return of(undefined);

        // TODO: (2015-08-19) Εδώ υπάρχει περίπτωση να έχουμε ενημερώσει εμείς τη βάση με EntryCompletion = true αλλά να μην έχει γίνει το ίδιο και στην ΑΑΔΕ λόγω αστοχίας κατά την ενημέρωση με την Α.Α.Δ.Ε
        //       Οπότε εδώ ίσως πρέπει να μπει και ένα Query στην Α.Α.Δ.Ε για το Status της εγγραφής και μετά να αποφασίσουμε αν ισχυεί το παρακατώ if.
        //
        // TODO: (2015-08-19) Να δούμε μήπως κάνουμε χρήση του response!!! Δηλαδή εμείς μπορεί να λέμε true στο πεδίο EntryCompletion αλλά έχουμε απάντηση??? Προς διερεύνηση!!!
        if (digitalClientCard.EntryCompletion == true)
            return of(digitalClientCard);

        this.loaderService.showAwaitLoader('Έξοδος Οχήματος');

        // Ενημερώνουμε τη βάση μας με το digitalClientCard.EntryCompletion = true.
        digitalClientCard.EntryCompletion = true;

        return this.store
            .dispatch(
                new DigitalClientCardGraphQLSchemaActions.Upsert(
                    digitalClientCard,
                    undefined,
                    this.digitalClientCardGQLQueryForEdit
                )
            )
            .pipe(
                concatMap(() => {
                    let updatedDigitalClientCard =
                        this.store.selectSnapshot(
                            DigitalClientCardGraphQLSchemaSelectors.UpsertSelector(
                                '',
                                ''
                            )
                        )?.data.Upsert ?? digitalClientCard;

                    // TODO: Εδώ πρέπει να δούμε τι θα κάνουμε αν αποτύχει η επόμενη κλήση αλλά σε εμάς έχουμε κρατήσει digitalClientCard.EntryCompletion = true.
                    // Στέλνουμε στην Α.Α.Δ.Ε την έξοδο του οχήματος.
                    return this.postDCLsToAADESchema
                        .UpdateDigitalClientCard(
                            updatedDigitalClientCard,
                            this.digitalClientCardGQLQueryForEdit
                        )
                        .pipe(
                            concatMap((digitalClientCard) => {
                                // Ενημερωση χρήστη με το απότελεσμα της ολοκλήρωσης της εγγραφής του ψηφιακού πελατολογίου (EntryCompletion = true).
                                const result =
                                    this.validateUpdateDigitalClientCard(
                                        digitalClientCard?.data
                                            .UpdateDigitalClientCard,
                                        options
                                    );

                                if (
                                    result != true || // Αν δεν ολοκληρώθηκε η εγγραφή του ψηφιακού πελατολογίου μη κάνεις τίποτα
                                    digitalClientCard?.data
                                        ?.UpdateDigitalClientCard
                                        ?.NonIssueInvoice == true
                                )
                                    return of(
                                        digitalClientCard?.data
                                            .UpdateDigitalClientCard
                                    );

                                return of(
                                    digitalClientCard?.data
                                        .UpdateDigitalClientCard
                                );
                            })
                        );
                }),
                finalize(() => this.loaderService.dismissAwaitLoader())
            );
    }

    /**
     * Ολοκλήρωνει την εγγραφή του Ψηφιακού πελατολογίου με την έξοδο του Οχήματος (digitalClientCard.EntryCompletion = true) και προχωράει στη συσχέτηση του παραστατικού με την εγγραφή (Αν δοθεί παραστατικό).
     * Έχει έρθει από το dcl-card.service.ts
     * @param dclOptions
     * @param invoice
     * @returns
     */
    completeDCLAndCorrelate(dclOptions: DCLCardOptions, invoice: InvoiceType) {
        debugger;
        const options = dclOptions; // as DCLCardOptions;

        const digitalClientCard =
            options.digitalClientCardTypeFormGroup?.getRawValue() as
                | DigitalClientCardType
                | undefined;

        if (
            digitalClientCard == undefined ||
            digitalClientCard._id == undefined
        )
            return of(undefined);

        return new Observable<DigitalClientCardType | undefined>((observer) => {
            const dclIdQueryParams = QueryFilter.FetchQueryParamsForEntityId(
                digitalClientCard?._id ?? '000000000000000000000000'
            );

            this.store
                .dispatch(
                    new DigitalClientCardGraphQLSchemaActions.Fetch(
                        digitalClientCardsQuery,
                        dclIdQueryParams,
                        '',
                        ''
                    )
                )
                .subscribe(() => {
                    const digitalClientCard = {
                        ...this.store.selectSnapshot(
                            DigitalClientCardGraphQLSchemaSelectors.FetchSelector(
                                '',
                                ''
                            )
                        )?.data?.Fetch?.[0]
                    };

                    if (
                        digitalClientCard == undefined ||
                        digitalClientCard?._id == undefined
                    ) {
                        observer.next(undefined);
                        observer.complete();
                        return of(undefined);
                    }

                    // Αν προσπαθήσιε να εκδόσει το δεύτερο παραστατικό αλλά η έξοδος έχει ήδη ολοκληρωθεί
                    // τότε απλά δέσε το δευτερο παραστατικό (αν έχει πάρει Μ.ΑΡ.Κ) στο ψηφιακό πελατολόγιο
                    if (digitalClientCard.EntryCompletion == true) {
                        if (
                            invoice.Response?.InvoiceMark != null &&
                            invoice.Response?.InvoiceMark != 0
                        ) {
                            // Tώρα να δέσουμε το τιμολόγιο με το παραστατικό (Το ξέρουμε ότι θα γίνει εδώ γιατί είμαστε στο Service dcl-card.service).
                            debugger;
                            this.correlateDigitalClientCard(
                                digitalClientCard,
                                invoice,
                                options
                            )
                                .pipe(
                                    concatMap(
                                        (correlateDigitalClientCardUpdate) => {
                                            observer.next(digitalClientCard);
                                            //observer.complete();
                                            return of(
                                                correlateDigitalClientCardUpdate
                                                    ?.data
                                                    .CorrelateDigitalClientCard
                                            );
                                        }
                                    ),
                                    finalize(() => {
                                        observer.complete();
                                    })
                                )
                                .subscribe(() => {});
                        }

                        //return of(digitalClientCard);
                        observer.next(digitalClientCard);
                        observer.complete();
                        return of(digitalClientCard);
                    }

                    this.loaderService.showAwaitDataSaveLoader(
                        'Έξοδος Οχήματος'
                    );

                    // Ενημερώνουμε τη βάση μας με το digitalClientCard.EntryCompletion = true.
                    digitalClientCard.EntryCompletion = true;

                    return this.store
                        .dispatch(
                            new DigitalClientCardGraphQLSchemaActions.Upsert(
                                digitalClientCard,
                                undefined,
                                this.digitalClientCardGQLQueryForEdit
                            )
                        )
                        .pipe(
                            concatMap(() => {
                                let updatedDigitalClientCard =
                                    this.store.selectSnapshot(
                                        DigitalClientCardGraphQLSchemaSelectors.UpsertSelector(
                                            '',
                                            ''
                                        )
                                    )?.data.Upsert ?? digitalClientCard;

                                // TODO: Εδώ πρέπει να δούμε τι θα κάνουμε αν αποτύχει η επόμενη κλήση αλλά σε εμάς έχουμε κρατήσει digitalClientCard.EntryCompletion = true.
                                // Στέλνουμε στην Α.Α.Δ.Ε την έξοδο του οχήματος.
                                return this.postDCLsToAADESchema
                                    .UpdateDigitalClientCard(
                                        updatedDigitalClientCard,
                                        this.digitalClientCardGQLQueryForEdit
                                    )
                                    .pipe(
                                        concatMap((digitalClientCard) => {
                                            // Ενημερωση χρήστη με το απότελεσμα της ολοκλήρωσης της εγγραφής του ψηφιακού πελατολογίου (EntryCompletion = true).
                                            const result =
                                                this.validateUpdateDigitalClientCard(
                                                    digitalClientCard?.data
                                                        .UpdateDigitalClientCard,
                                                    options
                                                );

                                            if (
                                                result != true || // Αν δεν ολοκληρώθηκε η εγγραφή του ψηφιακού πελατολογίου μη κάνεις τίποτα
                                                digitalClientCard?.data
                                                    ?.UpdateDigitalClientCard
                                                    ?.NonIssueInvoice == true
                                            ) {
                                                observer.next(
                                                    digitalClientCard?.data
                                                        .UpdateDigitalClientCard
                                                );
                                                //observer.complete();
                                                return of(
                                                    digitalClientCard?.data
                                                        .UpdateDigitalClientCard
                                                );
                                            }

                                            console.debug(
                                                'DCL Completed at: ',
                                                digitalClientCard.data
                                                    .UpdateDigitalClientCard
                                                    ?.CompletionDateTime
                                            );

                                            const invoiceMARK =
                                                invoice.Response?.InvoiceMark;
                                            if (
                                                invoiceMARK != null &&
                                                invoiceMARK != 0
                                            ) {
                                                // Tώρα να δέσουμε το τιμολόγιο με το παραστατικό (Το ξέρουμε ότι θα γίνει εδώ γιατί είμαστε στο Service dcl-card.service).
                                                return this.correlateDigitalClientCard(
                                                    digitalClientCard?.data
                                                        .UpdateDigitalClientCard,
                                                    invoice,
                                                    options
                                                ).pipe(
                                                    concatMap(
                                                        (
                                                            correlateDigitalClientCardUpdate
                                                        ) => {
                                                            //return of(digitalClientCard);
                                                            observer.next(
                                                                correlateDigitalClientCardUpdate
                                                                    ?.data
                                                                    .CorrelateDigitalClientCard
                                                            );
                                                            //observer.complete();
                                                            return of(
                                                                correlateDigitalClientCardUpdate
                                                                    ?.data
                                                                    .CorrelateDigitalClientCard
                                                            );
                                                        }
                                                    )
                                                );
                                            }

                                            // return of(
                                            //     digitalClientCard?.data
                                            //         .UpdateDigitalClientCard
                                            // );

                                            //return of(digitalClientCard);
                                            observer.next(
                                                digitalClientCard?.data
                                                    .UpdateDigitalClientCard
                                            );
                                            //observer.complete();
                                            return of(
                                                digitalClientCard?.data
                                                    .UpdateDigitalClientCard
                                            );
                                        })
                                    );
                            }),
                            finalize(() => {
                                this.loaderService.dismissAwaitLoader();
                                observer.complete();
                            })
                        )
                        .subscribe();
                });
        });
    }

    /**
     * @obsolete Αλλάζει η σειρά!!!!
     * Έξοδος Οχήματος. Εϊναι ο 3ος χρόνος στις προδιαγραφές (https://www.aade.gr/sites/default/files/2025-06/epixirisiaki_anal_psif_pelat_v.1.1_0.pdf)
     * 1. Ενημερώνουμε τη βάση με τις όποιες αλλαγές.
     * 2. Να δούμε αν υπάρχει καταχωρημένο παραστατικό (ή παραστατικά) για την εγγραφή του ψηφιακού πελατολογίου που μόλις καταχωρήσαμε.
     * 3. Αν Δεν υπάρχει Παραστατικό δεμένο στην εγγραφή ψηφιακού πελατολογίου τότε πρέπει να ζητήσουμε από τον χρήστη να καταχωρήσει ένα.
     * 4. Αν Υπάρχει παραστατικό ΧΩΡΙ΅ ΜΑΡΚ τότε ανοίγουμε το παραστατικό για Πληρωμή και Έκδοση.
     * 5. Αν Υπάρχει παραστατικό με ΜΑΡΚ τότε κάνουμε Correlate την εγγραφή.
     * @returns nothing
     */
    completeDCL2(
        dclOptions?: DCLCardOptions,
        invoice?: InvoiceType,
        digitalClientCard?: DigitalClientCardInputType
    ) {
        debugger;
        const options = dclOptions as DCLCardOptions;

        digitalClientCard =
            digitalClientCard ??
            (options.digitalClientCardTypeFormGroup?.getRawValue() as
                | DigitalClientCardInputType
                | undefined);
        if (
            digitalClientCard == undefined ||
            digitalClientCard._id == undefined
        )
            return;

        this.loaderService.showAwaitDataSaveLoader(
            'Οριστοκοποίηση Εγγραφής Ψηφιακού Πελατολογίου'
        );
        // Ενημέρωσε τη βάση με ότι αλλαγές έχουν γίνει στη φόρμα.
        this.store
            .dispatch(
                new DigitalClientCardGraphQLSchemaActions.Upsert(
                    digitalClientCard,
                    undefined,
                    this.digitalClientCardGQLQueryForEdit
                )
            )
            .pipe(
                concatMap(() => {
                    debugger;
                    const dclCard = this.store.selectSnapshot(
                        DigitalClientCardGraphQLSchemaSelectors.UpsertSelector(
                            '',
                            ''
                        )
                    )?.data?.Upsert;

                    // Φέρε το πρώτο παραστατικό που έχει έρθει επιτυχώς (InvoiceMark > 0). θα έχουμε πάντα ένα παραστατικό. Δεν υποστήρίζουμε 2 παραστατικά σε κάθε εγγραφή.
                    // (Όταν θα βάλουμε τη δυνατότητα να δένει 2 παραστατικά θα τα δένει από αλλού.)
                    const invoiceWithMark = dclCard?.Invoices?.find(
                        (invoice) => (invoice.Response?.InvoiceMark ?? 0) > 0
                    );

                    // Αν απαιτείται παραστατικό βάση του πεδίου ProvidedServiceCategory, τότε πρέπει να δούμε αν βρήκαμε κάποιο με _id
                    switch (digitalClientCard.ProvidedServiceCategory) {
                        case 1:
                        case 2:
                        case 3:
                            // Αν δεν υπάρχουν καθολού Παραστατικά τότε πρέπει να προσθέσουμε ένα. Οπότε ανοίγουμε τη φόρμα του νέου παρστατικού για να την ολοκληρώσει ο χρήστης.
                            if ((dclCard?.Invoices ?? []).length == 0) {
                                // ΠΡΟΣΟΧΗ: Όταν ανοίξει η φόρμα του νεόυ παραστατικού και ο χρήστης πατήσει έκδοση/διαβίβαση παραστατικού δίχως να έχει επιλέξει τρόπο πληρωμής
                                // το σύστημα πρέπει να του ζητήσει τρόπο πληρωμής και μετά να συνεχίσει στην έκδοση/διαβίβαση.
                                this.newInvoice(
                                    options,
                                    digitalClientCard,
                                    '11.2'
                                );

                                return of(undefined);
                                // return of({
                                //     data: {
                                //         UpdateDigitalClientCard1: undefined
                                //     }
                                // });
                            }

                            // Εδώ έχουμε δει ότι έχουμε τουλάχιστον ένα παραστατικό, οπότε να δούμε αν έχει ΜΑΡΚ. Αν δεν έχει πρέπει να ζητήσουμε από τον χρήστη να κάνει έκδοση παρστατικού.
                            // Για να ολοκληρωθεί σωστά μια εγγραφή ψηφιακού πελατολογίου τύπου 1 ή 2 ή 3 θα πρέπει να υπάρχει παραστατικό με ΜΑΡΚ.
                            // Το A.P.I του ψηφιακού πελατολογίου δεν κάνει έλεγχο αν ο χρήστης έχει καταχωρήσει το MARK, παρά μόνο αν έχει καταχωρήσει τον τύπο του παραστατικού
                            // που ΘΑ εκδόσει. Το παραστατικό μπορεί να εκδοθεί και σε 2ο χρόνο!!!!
                            if (invoiceWithMark == undefined) {
                                digitalClientCard.EntryCompletion = undefined;

                                // this.messageService.add({
                                //     summary: 'Μη έκδοση Φορολογικού Στοιχείου',
                                //     detail: "Δεν έχετε εκδώσει το απαραίτητο Φορολογικό Στοιχείο. Η 'Κατηγορία Παρεχόμενων Υπηρεσιών' που έχετε επιλέξει απαιτεί να εκδώσετε και να διαβιβάσετε στην Α.Α.Δ.Ε το ανάλογο παραστατικό.",
                                //     severity: 'error',
                                //     key: 'br'
                                // } as ToastMessageOptions);

                                // Άνοιξε τη φόρμα του 'ΠΡΩΤΟΥ' παραστατικού για να ολοκληρωθεί το παραστατικό.
                                this.editInvoice(options, digitalClientCard);

                                return of(undefined);
                                // return of({
                                //     data: {
                                //         UpdateDigitalClientCard: undefined
                                //     }
                                // });
                            }
                            break;
                    }

                    // Αν είμαστε σε αυτό το σημείο τότε είναι ολα εντάξει (δηλαδή υπάρχει παραστατικό με ΜΑΡΚ) για να προχωρήσουμε στην ολοκλήρωση της εγγραφής του ψηφιακού πελατολογίου.
                    //
                    // ζητάμε από τον Server μας να καλέσει τo DCL/UpdateCLient (DCL_UpdateCLientAsync) endPoint της Α.Α.Δ.Ε με
                    // το πεδίο EntryCompletion = digitalClientCard.EntryCompletion.
                    digitalClientCard.EntryCompletion = true;
                    debugger;
                    return this.store
                        .dispatch(
                            // Ενημέρωσε πάλι την εγγραφή με το πεδίο EntryCompletion (Για έξοδο Οχήματος)
                            new DigitalClientCardGraphQLSchemaActions.Upsert(
                                digitalClientCard,
                                undefined,
                                this.digitalClientCardGQLQueryForEdit
                            )
                        )
                        .pipe(
                            concatMap(() => {
                                let updatedDigitalClientCard =
                                    this.store.selectSnapshot(
                                        DigitalClientCardGraphQLSchemaSelectors.UpsertSelector(
                                            '',
                                            ''
                                        )
                                    )?.data.Upsert ?? digitalClientCard;

                                console.debug(
                                    'Correlate Digital Client Card: ',
                                    updatedDigitalClientCard
                                );

                                debugger;
                                // Mε αυτή τη κλήση γίνεται η ολοκλήρωση της εγγραφής του ψηφιακού πελατολογίου στην Α.Α.Δ.Ε.
                                //
                                // Εδώ ο Server θα καλέσει τo DCL/UpdateCLient (DCL_UpdateCLientAsync) endPoint της Α.Α.Δ.Ε με
                                // το πεδίο EntryCompletion = digitalClientCard.EntryCompletion.
                                //
                                // Αυτό σημαίνει ότι αν το πεδίο αυτό δεν πάρει την τιμη 'true' δεν θα γίνει ποτέ η ολoκλήρωση της εγγραφής
                                // η οποία απλά θα γίνεται update.
                                //
                                // Η Συσχέτηση παραστατικού θα γίνει μετά.
                                return this.postDCLsToAADESchema
                                    .UpdateDigitalClientCard(
                                        updatedDigitalClientCard,
                                        this.digitalClientCardGQLQueryForEdit
                                    )
                                    .pipe(
                                        concatMap((digitalClientCard) => {
                                            // Ενημερωση χρήστη με το απότελεσμα της ολοκλήρωσης της εγγραφής του ψηφιακού πελατολογίου.
                                            const result =
                                                this.validateUpdateDigitalClientCard(
                                                    digitalClientCard?.data
                                                        .UpdateDigitalClientCard,
                                                    options
                                                );

                                            if (
                                                result != true || // Αν δεν ολοκληρώθηκε η εγγραφή του ψηφιακού πελατολογίου μη κάνεις τίποτα
                                                digitalClientCard?.data
                                                    ?.UpdateDigitalClientCard
                                                    ?.NonIssueInvoice == true
                                            )
                                                return of(digitalClientCard);

                                            debugger;
                                            // Εδώ γίνεται η συσχέτηση της εγγραφής του ψηφιακού πελατολογίου με ένα παραστατικό.
                                            // Αν δεν υπάρχει παραστατικό τότε δεν μπορεί να γίνει η συχέτηση.
                                            // (Το παραστατικό που θα δεθεί έχει ορισθεί κατά την αποθήκευση ή έκδοση του παραστατικού πριν φθάσει ο κώδικας εδώ).
                                            // ΠΡΟΣΟΧΗ: Η έκδοση του παραστατικού για συσχέτηση με το ψηφιακό πελατολόγιο δεν είναι σειρακές εργασίες.
                                            //          Το παραστατικό θα εκδοθεί όταν είναι να εκδοθεί, άνεξάρτητα με το ψηφιακό πελατολόγιο.
                                            //          Την πρώτη φορά που κάνει έξοδο οχήματος ο χρήστης δεν υπάρχει παραστατικό οπότε ο κώδικας φεύγει από αυτη τη μέθοδο και ζητάει του χρήστη να
                                            //          καταχωρήσει το παραστατικό και να το έκδοσει αν θέλει.
                                            //          Μετά την έκδοση του παραστατικού θα ξαναμπεί ο κώδικας σε αυτή τη μέθοδο οπότε και θα έρθει σε αυτό το σημείο με το ΜΑΡΚ του παραστατικού.
                                            //          Δεν υπάρχει περίπτωση να έρθει ο κώδικας εδώ αν δεν υπάρχει ΜΑΡΚ, αυτό το εξασφαλίζουμε στις πρώτες γραμμές της μεθόδου αυτής.

                                            return this.correlateDigitalClientCard(
                                                digitalClientCard?.data
                                                    .UpdateDigitalClientCard,
                                                invoiceWithMark,
                                                options
                                            );

                                            //  this.postDCLsToAADESchema
                                            //     .CorrelateDigitalClientCard(
                                            //         digitalClientCard?.data
                                            //             .UpdateDigitalClientCard,
                                            //         this
                                            //             .digitalClientCardGQLQueryForEdit
                                            //     )
                                            //     .pipe(
                                            //         concatMap(
                                            //             (
                                            //                 correlateDigitalClientCard
                                            //             ) => {
                                            //                 this.validateDCLCorrelation(
                                            //                     correlateDigitalClientCard
                                            //                         ?.data
                                            //                         .CorrelateDigitalClientCard
                                            //                 );

                                            //                 return of(
                                            //                     correlateDigitalClientCard
                                            //                 );
                                            //             }
                                            //         )
                                            //     );

                                            // return of(digitalClientCard).pipe(
                                            //     concatMap(
                                            //         (
                                            //             digitalClientCard // correlateDigitalClientCardResponse
                                            //         ) => {
                                            //             // Συσχέτηση Παραστατικού

                                            //             if (digitalClientCard?.data?.UpdateDigitalClientCard?.NonIssueInvoice == true)
                                            //                 return of(digitalClientCard);

                                            //             // Εδώ γίνεται η συσχέτηση της εγγραφής του ψηφιακού πελατολογίου με ένα παραστατικό.
                                            //             // Αν δεν υπάρχει παραστατικό τότε δεν μπορεί να γίνει η συχέτηση.
                                            //             // (Το παραστατικό που θα δεθεί έχει ορισθεί κατά την αποθήκευση ή έκδοση του παραστατικού πριν φθάσει ο κώδικας εδώ).
                                            //             return this.postDCLsToAADESchema.CorrelateDigitalClientCard(
                                            //                 digitalClientCard
                                            //                     ?.data
                                            //                     .UpdateDigitalClientCard,
                                            //                 digitalClientCardGQLQueryForEdit
                                            //             );
                                            //         }
                                            //     )
                                            // );
                                        })
                                    );

                                // // Δες αν έχει ζητηθεί η μη έκδοση παραστατικού για να μη κάνει καμία συσχέτηση.
                                // // Προσοχή: Θεωρητικά, το επιτρέπει η Α.Α.Δ.Ε, να ορίσει ο χρήστης ότι δεν θέλει να εκδώσει παραστατικό!!!
                                // const nonIssueInvoice =
                                //     updatedDigitalClientCard.NonIssueInvoice;

                                // return (
                                //     nonIssueInvoice != true &&
                                //     updatedDigitalClientCard.EntryCompletion ==
                                //         true // Αν δεν είναι απαραίτητο το παραστατικό δεν συσχετίζεις παραστατικό.
                                //         ? // 2025/06/2025 Saridakis Antonis
                                //           // Mε αυτή τη κλήση γίνεται η ολοκλήρωση της εγγραφής του ψηφιακού πελατολογίου.
                                //           //
                                //           // Εδώ ο Server θα καλέσει τo DCL/UpdateCLient (DCL_UpdateCLientAsync) endPoint της Α.Α.Δ.Ε με
                                //           // το πεδίο EntryCompletion = digitalClientCard.EntryCompletion.
                                //           //
                                //           // Αυτό σημαίνει ότι αν το πεδίο αυτό δεν πάρει την τιμη 'true' δεν θα γίνει ποτέ η ολoκλήρωση της εγγραφής
                                //           // η οποία απλά θα γίνεται update.
                                //           //
                                //           // Η Συσχέτηση παραστατικού θα γίνει μετά.
                                //           this.postDCLsToAADESchema.UpdateDigitalClientCard(
                                //               updatedDigitalClientCard,
                                //               digitalClientCardGQLQueryForEdit
                                //           )
                                //         : of(undefined)
                                // ).pipe(
                                //     concatMap(
                                //         (
                                //             digitalClientCard // correlateDigitalClientCardResponse
                                //         ) => {
                                //             // εδώ μπορει να έχει έρθει

                                //             this.validateDCLCompletion(
                                //                 digitalClientCard?.data
                                //                     .UpdateDigitalClientCard
                                //             );

                                //             if (digitalClientCard == undefined)
                                //                 return of(undefined);

                                //             // Εδώ γίνεται η συσχέτηση της εγγραφής του ψηφιακού πελατολογίου με ένα παραστατικό.
                                //             // Αν δεν υπάρχει παραστατικό τότε δεν μπορεί να γίνει η συχέτηση.
                                //             // (Το παραστατικό που θα δεθεί έχει ορισθεί κατά την αποθήκευση ή έκδοση του παραστατικού πριν φθάσει ο κώδικας εδώ).
                                //             return this.postDCLsToAADESchema.CorrelateDigitalClientCard(
                                //                 digitalClientCard?.data
                                //                     .UpdateDigitalClientCard,
                                //                 digitalClientCardGQLQueryForEdit
                                //             );
                                //         }
                                //     )
                                // );
                            })
                        );
                }),
                finalize(() => {
                    this.loaderService.dismissAwaitLoader();
                    this.refreshData.emit(true);
                    // Ζητάμε από τον initiator να ανανεώσει τον πινακά του.
                    //options.onSave(options);
                })
            )
            .subscribe((result) => {
                debugger;
                // const lastResponse =
                //     digitalCardType?.data?.UpdateDigitalClientCard?.Responses?.sort(
                //         (a, b) => {
                //             const sort: number = 2; // 1 Ascending, 2 Descending
                //             if (a.ResponseDate == undefined)
                //                 return sort == 1 ? -1 : 1; // Ascending
                //             if (b.ResponseDate == undefined)
                //                 return sort == 1 ? 1 : -1; // Descending
                //             if (a.ResponseDate < b.ResponseDate)
                //                 return sort == 1 ? -1 : 1;
                //             if (a.ResponseDate > b.ResponseDate)
                //                 return sort == 1 ? 1 : -1;
                //             return 0;
                //         }
                //     )?.[0];
                // if (lastResponse?.Errors?.Error?.[0]?.Message != undefined) {
                //     this.messageService.add({
                //         summary: 'Αποτυχία ενημέρωσης Α.Α.Δ.Ε',
                //         detail: lastResponse?.Errors?.Error?.[0]?.Message ?? '',
                //         severity: 'error',
                //         key: 'br'
                //     } as ToastMessageOptions);
                // } else {
                //     if (digitalCardType?.data?.UpdateDigitalClientCard) {
                //         this.messageService.add({
                //             summary: 'Έξοδος Οχήματος',
                //             detail: 'Η εγγραφή οριστικοποιήθηκε επιτυχώς',
                //             severity: 'success',
                //             key: 'br'
                //         } as ToastMessageOptions);
                //         options.onSave(options);
                //     }
                // }
            });

        // // ζητάμε από τον Server μας να καλέσει τo DCL/UpdateCLient (DCL_UpdateCLientAsync) endPoint της Α.Α.Δ.Ε με
        // // // το πεδίο EntryCompletion = digitalClientCard.EntryCompletion.
        // digitalClientCard.EntryCompletion = true;

        // debugger;
        // switch (digitalClientCard.ProvidedServiceCategory) {
        //     case 1:
        //     case 2:
        //     case 3:
        //         //case 5: //TODO: Tcheck this Category !!!!!
        //         // this.dclFormGroup?.controls?.InvoiceKind?.setValue(
        //         //     undefined,
        //         //     { emitEvent: false }
        //         // );

        //         // Για να ολοκληρωθεί σωστά μια εγγραφή ψηφιακού πελατολογίου τύπου 1 ή 2 ή 3 θα πρέπει να υπάρχει παραστατικό με ΜΑΡΚ.
        //         // Το A.P.I του ψηφιακού πελατολογίου δεν κάνει έλεγχο αν ο χρήστης έχει καταχωρήσει το MARK, παρά μόνο αν έχει καταχωρήσει τον τύπο του παραστατικού
        //         // που ΘΑ εκδόσει. Το παραστατικό μπορεί να εκδοθεί και σε 2ο χρόνο!!!!
        //         if (digitalClientCard.InvoiceId == undefined) {
        //             digitalClientCard.EntryCompletion = undefined;

        //             this.messageService.add({
        //                 summary: 'Δεν βρέθηκε Φορολογικό Στοιχείο',
        //                 detail: "Δεν έχετε καταχωρήσει το απαραίτητο Φορολογικό Στοιχείο. Η 'Κατηγορία Παρεχόμενων Υπηρεσιών' που έχετε επιλέξει απαιτεί να εκδώσετε και να διαβιβάσετε στην Α.Α.Δ.Ε το ανάλογο παραστατικό.",
        //                 severity: 'error',
        //                 key: 'br'
        //             } as ToastMessageOptions);

        //             return;
        //         }
        //         break;
        // }

        // this.loaderService.showAwaitDataSaveLoader();

        // this.store
        //     .dispatch(
        //         new DigitalClientCardGraphQLSchemaActions.Upsert(
        //             digitalClientCard,
        //             undefined,
        //             digitalClientCardGQLQueryForEdit
        //         )
        //     )
        //     .pipe(
        //         concatMap(() => {
        //             let updatedDigitalClientCard =
        //                 this.store.selectSnapshot(
        //                     DigitalClientCardGraphQLSchemaSelectors.UpsertSelector(
        //                         '',
        //                         ''
        //                     )
        //                 )?.data.Upsert ?? digitalClientCard;

        //             console.debug(
        //                 'Correlate Digital Client Card: ',
        //                 updatedDigitalClientCard
        //             );

        //             const nonIssueInvoice =
        //                 updatedDigitalClientCard.NonIssueInvoice;

        //             // // Αν δεν υπάρχει δεμένο παραστατικό δημιούργησε ένα καινούριο
        //             // if (updatedDigitalClientCard.InvoiceId == undefined) {
        //             //     this.newInvoice(digitalClientCard, '11.2');
        //             //     return of({
        //             //         data: {
        //             //             UpdateDigitalClientCard:
        //             //                 updatedDigitalClientCard
        //             //         }
        //             //     });
        //             // }

        //             return (
        //                 nonIssueInvoice != true // Αν δεν είναι απαραίτητο το παραστατικό δεν συσχετήζεις παραστατικό.
        //                     ? // Εδώ γίνεται η συσχέτηση της εγγραφής του ψηφιακού πελατολογίου με ένα παραστατικό.
        //                       // Αν δεν υπάρχει παραστατικό τότε δεν μπορεί να γίνει η συχέτηση.
        //                       this.postDCLsToAADESchema.CorrelateDigitalClientCard(
        //                           updatedDigitalClientCard,
        //                           digitalClientCardGQLQueryForEdit
        //                       )
        //                     : of(undefined)
        //             ).pipe(
        //                 concatMap((digitalClientCardResponse) => {
        //                     // 2025/06/2025 Saridakis Antonis
        //                     // Mε αυτή τη κλήση γίνεται η ολοκλήρωση της εγγραφής του ψηφιακού πελατολογίου.
        //                     //
        //                     // Εδώ ο Server θα καλέσει τo DCL/UpdateCLient (DCL_UpdateCLientAsync) endPoint της Α.Α.Δ.Ε με
        //                     // το πεδίο EntryCompletion = digitalClientCard.EntryCompletion.
        //                     //
        //                     // Αυτό σημαίνει ότι αν το πεδίο αυτό δεν πάρει την τιμη 'true' δεν θα γίνει ποτέ η ολoκλήρωση της εγγραφής
        //                     // η οποία απλά θα γίνεται update.
        //                     return this.postDCLsToAADESchema.UpdateDigitalClientCard(
        //                         updatedDigitalClientCard,
        //                         digitalClientCardGQLQueryForEdit
        //                     );
        //                 })
        //             );
        //         }),
        //         finalize(() => this.loaderService.dismissAwaitLoader())
        //     )
        //     // .pipe(finalize(() => this.loaderService.dismissAwaitLoader()))
        //     .subscribe((digitalCardType) => {
        //         const lastResponse =
        //             digitalCardType?.data?.UpdateDigitalClientCard?.Responses?.sort(
        //                 (a, b) => {
        //                     const sort: number = 2; // 1 Ascending, 2 Descending
        //                     if (a.ResponseDate == undefined)
        //                         return sort == 1 ? -1 : 1; // Ascending

        //                     if (b.ResponseDate == undefined)
        //                         return sort == 1 ? 1 : -1; // Descending

        //                     if (a.ResponseDate < b.ResponseDate)
        //                         return sort == 1 ? -1 : 1;
        //                     if (a.ResponseDate > b.ResponseDate)
        //                         return sort == 1 ? 1 : -1;

        //                     return 0;
        //                 }
        //             )?.[0];

        //         if (lastResponse?.Errors?.Error?.[0]?.Message != undefined) {
        //             this.messageService.add({
        //                 summary: 'Αποτυχία ενημέρωσης Α.Α.Δ.Ε',
        //                 detail: lastResponse?.Errors?.Error?.[0]?.Message ?? '',
        //                 severity: 'error',
        //                 key: 'br'
        //             } as ToastMessageOptions);
        //         } else {
        //             options.onSave(options);
        //         }
        //     });

        // this.postDCLsToAADESchema
        //     .CorrelateDigitalClientCard(
        //         digitalClientCard,
        //         DigitalClientCardTypeQuery.GQLQuery()
        //     )
        //     .pipe(finalize(() => this.loaderService.dismissAwaitLoader()))
        //     .subscribe(() => {
        //         options.onSave(options);
        //     });
    }

    correlateDigitalClientCard(
        digitalClientCard: DigitalClientCardType | undefined,
        invoice: InvoiceType | undefined,
        options?: DCLCardOptions
    ) {
        return this.postDCLsToAADESchema
            .CorrelateDigitalClientCard(
                digitalClientCard,
                invoice,
                this.digitalClientCardGQLQueryForEdit
            )
            .pipe(
                concatMap((correlateDigitalClientCard) => {
                    this.validateDCLCorrelation(
                        invoice,
                        options,
                        correlateDigitalClientCard?.data
                            .CorrelateDigitalClientCard
                    );

                    return of(correlateDigitalClientCard);
                })
            );
    }

    /**
     * 2025-09-06: Μεταβολή Παραστατικού.
     * @param dclCardsDialogConfigDataOptions
     * @param dclCard
     */
    editInvoice(
        dclCardsDialogConfigDataOptions: DCLCardOptions,
        dclCard: DigitalClientCardType
    ) {
        debugger;

        const invoiceType =
            dclCard?.Invoices?.[0]?.TypeCode == '1.1' ||
            dclCard?.Invoices?.[0]?.TypeCode == '2.1'
                ? '2.1'
                : '11.2'; //= 'invoice' ? '2.1' : '11.2' // 11.2 ΑΠΥ

        const dialogRef = this.dialogService.open<InvoiceCardComponent>(
            InvoiceCardComponent,
            {
                header: 'Παραστατικό',
                height: '95vh',
                width: '95vw',
                modal: true,
                resizable: true,
                draggable: true,
                closeOnEscape: false,
                closable: true,
                focusOnShow: false,
                maximizable: true,

                templates: {
                    footer: InvoiceCardFooterComponent
                },
                data: {
                    mode: 'edit',
                    sourceModal: true,
                    dclId: dclCard?._id,
                    dclCard: dclCard,
                    DCLInvoiceTypeCode: invoiceType, // == 'invoice' ? '2.1' : '11.2', // 11.2 ΑΠΥ dclCard?.Invoices?.[index]?.TypeCode, //  invoiceTypeCode,
                    // ας στείλουμε τα παραστατικά που θέλουμε να δούμε.
                    // Αν δεν υπάρχουν θα στείλουμε undefined αλλά πάντα θα έχει 2 εγγραφές γιατί θέλουμε να εκδίδουμε τουλάχιστον 2 παραστατικά σε κάθε εγγραφή ψηφιακού πελατολογίου.
                    invoices: dclCard.Invoices,
                    onSave: (options: InvoiceOptions, unattented?: boolean) => {
                        console.debug(
                            'Saved & Complete from DCL Card Completion (EditInvoice)'
                        );
                        if (unattented != true) dialogRef.close();
                        // Να ενημερώσουμε τη λίστα με τις καρτέλες του DCL να κλείσει το modal του DCL Card και να κάνει Refresh
                        //const dclCardsOptions = dialogConfig as DCLCardOptions;
                        dclCardsDialogConfigDataOptions.onDCLCardSave(
                            dclCardsDialogConfigDataOptions
                        );
                        //if (this.event) this.onLazyLoad(this.event);
                    },
                    onDoPOSTransaction: (
                        options: InvoiceOptions,
                        lastSalePOSTransaction:
                            | POSTerminalTransactionType
                            | undefined,
                        POSResponse: POSTerminalResponseType | undefined
                    ) => {
                        debugger;
                        dialogRef.close();
                        // Τώρα οριστικοποίησε την εγγραφή γιατί ολοκληρώθηκε επιτυχώς η διαβίβαση του παραστατικού που έγινε σε 2ο χρόνο γιατί είχε ζητηθεί πληρωμή με κάρτα.
                        //this.completeDCL();
                        // TODO: Να το δούμε γιατί εδώ μάλλον πρέπει να μπει αυτό this.completeDCLAndCorrelate(dialogConfig, invoice)
                        // alert('Test Edit Invoice');
                        this.completeDCL2(dclCardsDialogConfigDataOptions);
                    },
                    onSentToAADE: (
                        invoiceptions: InvoiceOptions,
                        invoice: InvoiceType
                    ) => {
                        debugger;
                        console.debug(
                            'send to aade from DCL Card Completion !!!'
                        );
                        // Τώρα οριστικοποίησε την εγγραφή γιατί ολοκληρώθηκε επιτυχώς η διαβίβαση του παραστατικού.
                        // Εδώ έρχεται όταν δεν επιλέξει πληρωμή με κάρτα ο χρήστης και είμασε σε μεταβολή παραστατικού.

                        //dialogRef.close();

                        timer(100).subscribe(() => {
                            this.completeDCLAndCorrelate(
                                dclCardsDialogConfigDataOptions,
                                invoice
                            )
                                .pipe(
                                    finalize(() => {
                                        // alert(
                                        //     'completeDCLAndCorrelate Edit Done!'
                                        // );
                                        // debugger;
                                        // debugger;
                                        // Ενημέρωσε τη καρτέλα των παραστατικών ότι ολοκληρώθηκε η απόστολή στην ΑΑΔΕ οπότε ανανέωσε τα δεδομένα σου για να μπορέσει να δείξει σωστά τα κουμπία όπως π.χ να μην δείξει το κουμπί 'Συσχέτιση...'
                                        invoiceptions.onCompleteCorrelate?.(
                                            invoiceptions,
                                            invoice
                                        );

                                        dclCardsDialogConfigDataOptions?.onRefresh(
                                            dclCardsDialogConfigDataOptions
                                        );
                                    })
                                )
                                .subscribe();
                        });

                        // if (this.event) this.onLazyLoad(this.event);
                    }
                }
            } as DynamicDialogConfig<InvoiceOptions>
        );
    }

    newInvoice(
        dclCardsDialogConfigDataOptions: DCLCardOptions,
        dclCard: DigitalClientCardType,
        invoiceTypeCode: string
    ) {
        debugger;
        const invoiceDialogRef = this.dialogService.open<InvoiceCardComponent>(
            InvoiceCardComponent,
            {
                header: 'Νέο Παραστατικό',
                height: '95vh',
                width: '95vw',
                modal: true,
                resizable: true,
                draggable: true,
                closable: true,
                maximizable: true,

                templates: {
                    footer: InvoiceCardFooterComponent
                },
                data: {
                    mode: 'add',
                    sourceModal: true,
                    dclId: dclCard?._id,
                    dclCard: dclCard,
                    DCLInvoiceTypeCode: invoiceTypeCode,
                    // ας στείλουμε τα παραστατικά που θέλουμε να δούμε.
                    // Αν δεν υπάρχουν θα στείλουμε undefined αλλά πάντα θα έχει 2 εγγραφές γιατί θέλουμε να εκδόσουμε τουλάχιστον 2 παραστατικά σε κάθε εγγραφή ωηφιακού πελατολογίου.
                    invoices: dclCard.Invoices,

                    onSave: (options: InvoiceOptions, unattented?: boolean) => {
                        console.debug(
                            'Saved & Complete from DCL Card (New Invoice)'
                        );
                        if (unattented != true) invoiceDialogRef.close();

                        // Να ενημερώσουμε τη λίστα με τις καρτέλες του DCL να κλείσει το modal του DCL Card και να κάνει Refresh
                        //const dclCardsOptions = dialogConfig as DCLCardOptions;
                        dclCardsDialogConfigDataOptions.onDCLCardSave(
                            dclCardsDialogConfigDataOptions
                        );

                        // if (this.event) this.onLazyLoad(this.event);
                    },
                    onDoPOSTransaction: (
                        options: InvoiceOptions,
                        lastSalePOSTransaction:
                            | POSTerminalTransactionType
                            | undefined,
                        POSResponse: POSTerminalResponseType | undefined
                    ) => {
                        debugger;
                        invoiceDialogRef.close();
                        // Τώρα οριστικοποίησε την εγγραφή γιατί ολοκληρώθηκε επιτυχώς η διαβίβαση του παραστατικού που έγινε σε 2ο χρόνο γιατί είχε ζητηθεί πληρωμή με κάρτα.
                        //this.completeDCL();
                        // TODO: Να το δούεμ γιατί εδώ μάλλον πρέπει να μπει αυτό this.completeDCLAndCorrelate(dialogConfig, invoice)
                        // alert('Test Add Invoice');
                        this.completeDCL2(dclCardsDialogConfigDataOptions);
                    },
                    onSentToAADE: (
                        invoiceOptions: InvoiceOptions,
                        invoice: InvoiceType
                    ) => {
                        // εδώ έρχεται από την options.onSentToAADE?.(options, invoice); του InvoiceCardFooter.component.ts γραμμή 1084
                        //dialogRef.close();

                        debugger;
                        // Τώρα οριστικοποίησε την εγγραφή γιατί ολοκληρώθηκε επιτυχώς η διαβίβαση του παραστατικού.
                        // Εδώ έρχεται όταν δεν επιλέξει πληρωμή με κάρτα ο χρήστης και είμαστε σε νέο παραστατικό μέσα από το DCL Card και το κουμπάκι έξοδος.
                        //this.completeDCL();

                        // // δώσε λίγο χρόνο για να κάνει refresh το UI και να κλείσει τα Modals που έχει ανοιχτά.
                        timer(100).subscribe(() => {
                            // ddddd
                            this.completeDCLAndCorrelate(
                                dclCardsDialogConfigDataOptions,
                                invoice
                            )
                                .pipe(
                                    finalize(() => {
                                        // alert(
                                        //     'completeDCLAndCorrelate New Done!'
                                        // );

                                        // Ενημέρωσε τη καρτέλα των παραστατικών ότι ολοκληρώθηκε η απόστολή στην ΑΑΔΕ οπότε ανανέωσε τα δεδομένα σου για να μπορέσει να δείξει σωστά τα κουμπία όπως π.χ να μην δείξει το κουμπί 'Συσχέτιση...'
                                        invoiceOptions.onCompleteCorrelate?.(
                                            invoiceOptions,
                                            invoice
                                        );

                                        dclCardsDialogConfigDataOptions?.onRefresh(
                                            dclCardsDialogConfigDataOptions
                                        );
                                    })
                                )
                                .subscribe();
                        });

                        // const dclOptions = this.dialogConfig as DCLCardOptions;
                        // dclOptions.onSave(dclOptions);

                        // if (this.event) this.onLazyLoad(this.event);
                    }
                }
            } as DynamicDialogConfig<InvoiceOptions>
        );

        //invoiceDialogRef?.onClose?.pipe(takeUntil())
    }

    validateDCLCorrelation(
        updatedInvoice: InvoiceType | undefined,
        options?: DCLCardOptions,
        dclCard?: DigitalClientCardType
    ) {
        if (dclCard) {
            // const lastInvoiceIndex = (dclCard.Invoices?.length ?? 0) - 1;

            // const lastInvoiceResponse =
            //     dclCard.Invoices?.[lastInvoiceIndex]?.Response;
            const invoice = dclCard.Invoices?.find(
                (filter) => filter._id == updatedInvoice?._id
            );

            const statusCode = invoice?.DCLCorrelationResponse?.StatusCode;
            switch (statusCode?.toLocaleLowerCase()) {
                case 'success':
                    this.messageService.add({
                        summary: 'Συσχέτιση Παραστατικού',
                        detail: 'Η Συσχέτιση του Παραστατικού με την Εγγραφή του Ψηφιακού Πελατολογίου ολοκληρώθηκε επιτυχώς',
                        severity: 'success',
                        key: 'br'
                    } as ToastMessageOptions);

                    // Αυτό θα κλείσει το παράθυρο της εγγραφής του ωηφιακού πελατολογίου. Το θέλουμε αν όλα πάνε καλά.
                    //options?.onDCLCardSave(options);
                    break;
                default:
                    this.messageService.add({
                        summary: 'Αποτυχία Συσχέτισης Παραστατικού',
                        detail: invoice?.DCLCorrelationResponse?.Errors
                            ?.Error?.[0]?.Message,
                        severity: 'error',
                        key: 'br'
                    } as ToastMessageOptions);
                    break;
            }
        }
    }

    /**
     * Βρίσκει την τελυταία απάντηση από τις κλήσεις στην Α.Α.Δ.Ε για το ψηφιακό πελατολόγιο
     * και ενημερώνει τον χρήστη αν ολοκληρώθηκε επιτηχώς ή όχι η Έξοδος Οχήματος.
     * @param digitalCardType
     */
    validateUpdateDigitalClientCard(
        digitalCardType?: DigitalClientCardType,
        options?: DCLCardOptions
    ) {
        const lastResponse = digitalCardType?.Responses?.sort((a, b) => {
            const sort: number = 2; // 1 Ascending, 2 Descending
            if (a.ResponseDate == undefined) return sort == 1 ? -1 : 1; // Ascending

            if (b.ResponseDate == undefined) return sort == 1 ? 1 : -1; // Descending

            if (a.ResponseDate < b.ResponseDate) return sort == 1 ? -1 : 1;
            if (a.ResponseDate > b.ResponseDate) return sort == 1 ? 1 : -1;

            return 0;
        })?.[0];

        if (lastResponse?.Errors?.Error?.[0]?.Message != undefined) {
            this.messageService.add({
                summary: 'Έξοδος Οχήματος - Αποτυχία ενημέρωσης Α.Α.Δ.Ε',
                detail: lastResponse?.Errors?.Error?.[0]?.Message ?? '',
                severity: 'error',
                key: 'br'
            } as ToastMessageOptions);
        } else {
            if (digitalCardType) {
                this.messageService.add({
                    summary: 'Έξοδος Οχήματος',
                    detail: 'Η ενημέρωση της Α.Α.Δ.Ε για την έξοδο του οχήματος ολοκληρώθηκε επιτυχώς. Ακολουθεί η διασύνδεση του παραστατικού με το Ψηφιακό Πελατολόγιο.',
                    severity: 'success',
                    key: 'br'
                } as ToastMessageOptions);
                return true;
                //options?.onSave(options);
            }
        }

        return false;
    }

    digitalClientCardGQLQueryForEdit = DigitalClientCardTypeQuery.GQLQuery({
        Garage: DCLGarageUseTypeQuery.GQLQuery(),
        RentalUse: DCLRentalUseTypeQuery.GQLQuery(),
        ParkingUse: DCLParkingCarWashUseTypeQuery.GQLQuery(),
        Customer: CustomerTypeQuery.GQLQuery({
            Addresses: AddressTypeQuery.GQLQuery({
                City: CityTypeQuery.GQLQuery()
            }),
            FirstName: FirstNameTypeQuery.GQLQuery(),
            FatherName: FirstNameTypeQuery.GQLQuery(),
            InfoNotes: InfoNoteTypeQuery.GQLQuery(),
            OccupationActivity: OccupationActivityTypeQuery.GQLQuery(),
            ContactDetails: ContactDetailTypeQuery.GQLQuery({
                Address: AddressTypeQuery.GQLQuery({
                    City: CityTypeQuery.GQLQuery()
                })
            }),
            // Courier: CourierTypeQuery.GQLQuery(),
            FinancialDetails: FinancialDetailTypeQuery.GQLQuery(),
            TraderProfitMargin: TraderProfitMarginTypeQuery.GQLQuery(),
            CTAXOffice: TAXOfficeTypeQuery.GQLQuery(),
            Subjects: CustomersToEntitiesSubjectTypeQuery.GQLQuery({
                Subject: EntitySubjectInfoTypeQuery.GQLQuery()
            })
        }),
        Invoices: InvoiceTypeQuery.GQLQuery({
            Response: ProviderResponseTypeQuery.GQLQuery(),
            Responses: ProviderResponseTypeQuery.GQLQuery(),
            DCLCorrelationResponse: DCLResponseTypeQuery.GQLQuery({
                Errors: ResponseTypeErrorTypeQuery.GQLQuery({
                    Error: ProviderResponseErrorTypeQuery.GQLQuery()
                })
            })
        }),
        Responses: DCLResponseTypeQuery.GQLQuery({
            Errors: ResponseTypeErrorTypeQuery.GQLQuery({
                Error: ProviderResponseErrorTypeQuery.GQLQuery()
            }),
            DCLCorrelations: DCLCorrelationTypeQuery.GQLQuery(),
            UsersActions: UsersActionsTypeQuery.GQLQuery()
        })
    });
}
